import React, { memo, useCallback, useEffect, useRef } from 'react'

import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import EditCanvas from 'Pods/ThreeDStudio/Shared/ImageEditor/EditCanvas'
import { restoreUrl } from 'Pods/ThreeDStudio/Shared/Utils/imageEditUrl'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import {
  closeFullImageEditor,
  getOpenedImageSessionId,
  setOpenedImageSessionId,
} from 'Pods/ThreeDStudio/Store/actions/ui'

import HeaderActions from './Components/HeaderActions/HeaderActions'

// The editor opened from the library, filling the whole area right of the sidebar: no
// Generate form, no viewer and no viewer rail. It renders the same editor components the
// AI Assistant's Edit tab does, against its own image and session so neither surface can
// disturb the other.
//
// Opening it is in-place state rather than a route, so there is no browser Back out of it.
// Opened from the library, the sidebar is the exit and a route change clears the flag
// (useCloseImageEditorOnNavigate). Opened by the Image Generator's Create page, the header's
// Back control returns there. It is a mode change with no page load, so focus moves
// into the named region on mount rather than staying on a control the user can no longer
// see. It is deliberately not role="dialog": nothing dismisses it.
function FullImageEditor() {
  const t = useTranslation()
  const image = useThreeDStudioStore(state => state.ui.openedImage)
  const sessionId = useThreeDStudioStore(state => state.ui.openedImageSessionId)
  const runId = useThreeDStudioStore(state => state.ui.openedImageRunId)
  const canReturnToCreate = useThreeDStudioStore(state => state.ui.openedImageCanReturnToCreate)
  const returnUrl = useThreeDStudioStore(state => state.ui.openedImageReturnUrl)
  const surfaceRef = useRef(null)

  useEffect(() => {
    surfaceRef.current?.focus()
  }, [])

  // Back to the Create page also puts back the address bar the image's URL replaced there.
  const handleStartNew = useCallback(() => {
    closeFullImageEditor()
    restoreUrl(returnUrl)
  }, [returnUrl])

  const imageName = image?.alt ?? ''
  const imageId = image?.id

  // Delete removes the run that produced the version on the canvas. Edits carry their own
  // run id; the opened image falls back to the one it was opened with. The lineage's
  // original was made by a run the chain does not index, so it resolves to null and
  // Delete stays disabled rather than removing some other run. An image opened without a run
  // id is not the viewer's to delete (another user's, or one the gallery's Edit opened), so
  // Delete is left out until the viewer's own edit is on the canvas.
  const renderHeaderActions = useCallback(
    (currentImage) => {
      const isOpenedImage = currentImage != null && String(currentImage.id) === String(imageId)
      const currentRunId = currentImage?.runId ?? (isOpenedImage ? runId : null)

      return (
        <HeaderActions
          image={currentImage}
          runId={currentRunId}
          imageName={imageName}
          isDeletable={runId != null || currentRunId != null}
        />
      )
    },
    [runId, imageId, imageName],
  )

  if (!image) {
    return null
  }

  return (
    <section
      ref={surfaceRef}
      className="full-image-editor"
      aria-label={t('fullImageEditor.regionLabel')}
      tabIndex={-1}
    >
      {/* Keyed on the image so picking another one from the library remounts the editor:
          the mask and the version chain belong to the image that was open. */}
      <EditCanvas
        key={image.id}
        image={image}
        sessionId={sessionId}
        getSessionId={getOpenedImageSessionId}
        onSessionCreated={setOpenedImageSessionId}
        isStartNewHidden={!canReturnToCreate}
        onStartNew={handleStartNew}
        renderHeaderActions={renderHeaderActions}
        customClass="full-image-editor__canvas"
        composerClass="full-image-editor__composer"
      />
    </section>
  )
}

export default memo(FullImageEditor)
